<template>
  <div class="account-page">
    <!-- Header -->
    <div class="page-header">
      <div class="header-content">
        <div>
          <h1 class="page-title">Quản lý Tài khoản</h1>
          <p class="page-subtitle">
            Quản lý người dùng admin và phân quyền hệ thống
          </p>
        </div>
        <button @click="showCreateModal = true" class="btn btn-primary">
          <i class="icon-plus"></i>
          Tạo tài khoản mới
        </button>
      </div>
    </div>

    <!-- Statistics Cards -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon bg-blue">
          <i class="fa fa-users"></i>
        </div>
        <div class="stat-info">
          <p class="stat-label">Tổng số tài khoản</p>
          <h3 class="stat-value">{{ statistics.total }}</h3>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon bg-green">
          <i class="fa fa-check-circle"></i>
        </div>
        <div class="stat-info">
          <p class="stat-label">Đang hoạt động</p>
          <h3 class="stat-value">{{ statistics.active }}</h3>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon bg-orange">
          <i class="fa fa-pause-circle"></i>
        </div>
        <div class="stat-info">
          <p class="stat-label">Tạm ngưng</p>
          <h3 class="stat-value">{{ statistics.suspended }}</h3>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon bg-red">
          <button class="btn-reset-icon" @click="viewUserTrashed()">
            <i class="fa fa-trash"></i>
          </button>
        </div>
        <div class="stat-info">
          <p class="stat-label">Đã xóa</p>
          <h3 class="stat-value">{{ statistics.deleted }}</h3>
        </div>
      </div>
    </div>

    <!-- Filters and Search -->
    <div class="filters-section">
      <div class="search-box">
        <i class="icon-search"></i>
        <input
          v-model="filters.search"
          type="text"
          placeholder="Tìm kiếm theo tên, email, username..."
          @input="debounceSearch"
        />
      </div>

      <div class="filter-group">
        <select
          v-model="filters.role"
          @change="fetchUsers"
          class="filter-select"
        >
          <option value="">Tất cả vai trò</option>
          <option value="super_admin">Super Admin</option>
          <option value="admin">Admin</option>
          <option value="manager">Manager</option>
          <option value="staff">Nhân viên</option>
        </select>

        <select
          v-model="filters.status"
          @change="fetchUsers"
          class="filter-select"
        >
          <option value="">Tất cả trạng thái</option>
          <option value="active">Đang hoạt động</option>
          <option value="inactive">Không hoạt động</option>
          <option value="suspended">Tạm ngưng</option>
        </select>

        <button @click="resetFilters" class="btn btn-outline">
          <i class="icon-refresh"></i>
          Đặt lại
        </button>
      </div>
    </div>

    <!-- Users Table -->
    <div class="table-container">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Đang tải dữ liệu...</p>
      </div>

      <table v-else class="users-table">
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                @change="toggleSelectAll"
                :checked="isAllSelected"
              />
            </th>
            <th>Người dùng</th>
            <th>Vai trò</th>
            <th>Trạng thái</th>
            <th>Quyền</th>
            <th>Đăng nhập cuối</th>
            <th>Thao tác</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="user in users"
            :key="user.id"
            :class="{ selected: selectedUsers.includes(user.id) }"
          >
            <td>
              <input type="checkbox" :value="user.id" v-model="selectedUsers" />
            </td>
            <td>
              <div class="user-info">
                <div class="user-avatar">
                  <img
                    v-if="user.avatar"
                    :src="getAvatarUrl(user.avatar)"
                    :alt="user.name"
                  />
                  <div v-else class="avatar-placeholder">
                    {{ getInitials(user.name) }}
                  </div>
                </div>
                <div class="user-details">
                  <p class="user-name">{{ user.name }}</p>
                  <p class="user-email">{{ user.email }}</p>
                  <p class="user-username">@{{ user.username }}</p>
                </div>
              </div>
            </td>
            <td>
              <span class="role-badge" :class="`role-${user.role}`">
                {{ getRoleLabel(user.role) }}
              </span>
            </td>
            <td>
              <span class="status-badge" :class="`status-${user.status}`">
                {{ getStatusLabel(user.status) }}
              </span>
            </td>
            <td>
              <div class="permissions-preview">
                <span
                  v-if="user.permissions.length > 0"
                  class="permission-count"
                >
                  {{ user.permissions.length }} quyền
                </span>
                <span v-else class="no-permissions">Chưa có quyền</span>
              </div>
            </td>
            <td>
              <div v-if="user.last_login_at" class="last-login">
                <p class="login-time">{{ formatDate(user.last_login_at) }}</p>
                <p class="login-ip">{{ user.last_login_ip }}</p>
              </div>
              <span v-else class="text-muted">Chưa đăng nhập</span>
            </td>
            <td>
              <div class="action-buttons">
                <button
                  @click="viewUser(user)"
                  class="btn-icon"
                  title="Xem chi tiết"
                >
                  <i class="fa fa-eye"></i>
                </button>
                <button
                  @click="editUser(user)"
                  class="btn-icon"
                  title="Chỉnh sửa"
                >
                  <i class="fa fa-edit"></i>
                </button>
                <button
                  @click="managePermissions(user)"
                  class="btn-icon"
                  title="Phân quyền"
                >
                  <i class="fa fa-key"></i>
                </button>
                <button
                  @click="toggleStatus(user)"
                  class="btn-icon"
                  :class="
                    user.status === 'active' ? 'text-warning' : 'text-success'
                  "
                  :title="user.status === 'active' ? 'Tạm ngưng' : 'Kích hoạt'"
                >
                  <i
                    :class="
                      user.status === 'active' ? 'fa fa-pause' : 'fa fa-play'
                    "
                  ></i>
                </button>
                <button
                  v-if="user.id !== currentUser.id"
                  @click="deleteUser(user)"
                  class="btn-icon text-danger"
                  title="Xóa"
                >
                  <i class="fa fa-trash"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Empty State -->
      <div v-if="!loading && users.length === 0" class="empty-state">
        <i class="icon-users-slash"></i>
        <h3>Không tìm thấy người dùng</h3>
        <p>Thử điều chỉnh bộ lọc hoặc tạo tài khoản mới</p>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="pagination.total > 0" class="pagination">
      <div class="pagination-info">
        Hiển thị {{ pagination.from }} - {{ pagination.to }} trong tổng số
        {{ pagination.total }} tài khoản
      </div>
      <div class="pagination-controls">
        <button
          @click="changePage(pagination.current_page - 1)"
          :disabled="pagination.current_page === 1"
          class="btn btn-outline btn-sm"
        >
          Trước
        </button>

        <button
          v-for="page in visiblePages"
          :key="page"
          @click="changePage(page)"
          :class="[
            'btn',
            'btn-sm',
            page === pagination.current_page ? 'btn-primary' : 'btn-outline',
          ]"
        >
          {{ page }}
        </button>

        <button
          @click="changePage(pagination.current_page + 1)"
          :disabled="pagination.current_page === pagination.last_page"
          class="btn btn-outline btn-sm"
        >
          Sau
        </button>
      </div>
    </div>

    <!-- Modals -->
    <CreateUserModal
      v-if="showCreateModal"
      @close="showCreateModal = false"
      @created="handleUserCreated"
    />

    <EditUserModal
      v-if="showEditModal"
      :user="selectedUser"
      @close="showEditModal = false"
      @updated="handleUserUpdated"
    />

    <UserDetailModal
      v-if="showDetailModal"
      :user="selectedUser"
      @close="showDetailModal = false"
      @edit="editUser"
    />

    <PermissionsModal
      v-if="showPermissionsModal"
      :user="selectedUser"
      :all-permissions="permissions"
      @close="showPermissionsModal = false"
      @updated="handlePermissionsUpdated"
    />

    <ConfirmDialog
      v-if="showConfirmDialog"
      :title="confirmDialog.title"
      :message="confirmDialog.message"
      :type="confirmDialog.type"
      @confirm="confirmDialog.onConfirm"
      @cancel="showConfirmDialog = false"
    />
    <UserTrashedModal
      v-if="showUserTrashed"
      :user="selectedUser"
      @close="showUserTrashed = false"
      @restored="handleRestoreUser"
    />
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from "vue";
import { useToast } from "@/composables/useToast";
import { useAuth } from "@/composables/useAuth";
import { adminApi } from "@/services/api";
import CreateUserModal from "@/components/modal/CreateUserModal.vue";
import EditUserModal from "@/components/modal/EditUserModal.vue";
import UserDetailModal from "@/components/modal/UserDetailModal.vue";
import PermissionsModal from "@/components/modal/PermissionsModal.vue";
import ConfirmDialog from "@/components/modal/ConfirmDialog.vue";
import UserTrashedModal from "@/components/modal/UserTrashedModal.vue";

export default {
  name: "AccountPage",
  components: {
    CreateUserModal,
    EditUserModal,
    UserDetailModal,
    PermissionsModal,
    ConfirmDialog,
    UserTrashedModal,
  },
  setup() {
    const toast = useToast();
    const { currentUser } = useAuth();

    // State
    const loading = ref(false);
    const users = ref([]);
    const permissions = ref([]);
    const selectedUsers = ref([]);
    const selectedUser = ref(null);
    const statistics = reactive({
      total: 0,
      active: 0,
      inactive: 0,
      suspended: 0,
      deleted: 0,
    });

    const filters = reactive({
      search: "",
      role: "",
      status: "",
    });

    const pagination = reactive({
      current_page: 1,
      last_page: 1,
      per_page: 20,
      total: 0,
      from: 0,
      to: 0,
    });

    const showCreateModal = ref(false);
    const showEditModal = ref(false);
    const showDetailModal = ref(false);
    const showPermissionsModal = ref(false);
    const showConfirmDialog = ref(false);
    const showUserTrashed = ref(false);
    const confirmDialog = reactive({
      title: "",
      message: "",
      type: "danger",
      onConfirm: () => {},
    });

    const isAllSelected = computed(() => {
      const total = users.value?.length || 0;
      const selected = selectedUsers.value?.length || 0;

      return total > 0 && selected === total;
    });

    const visiblePages = computed(() => {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(
        1,
        pagination.current_page - Math.floor(maxVisible / 2),
      );
      let end = Math.min(pagination.last_page, start + maxVisible - 1);

      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    });

    // Methods
    const fetchUsers = async () => {
      loading.value = true;
      try {
        pagination.current_page = 1;
        const response = await adminApi.getUsers({
          page: pagination.current_page,
          per_page: pagination.per_page,
          search: filters.search,
          role: filters.role,
          status: filters.status,
        });
        users.value = response.data.data.data;

        const { ...paginationMeta } = response.data.data;
        Object.assign(pagination, paginationMeta);
      } catch (error) {
        toast.error("Lỗi khi tải danh sách người dùng");
        if (error.response.data && error.response.status === 403) {
          toast.error("Bạn không có quyền truy cập cần thiết");
        }
        console.error(error);
      } finally {
        loading.value = false;
      }
    };

    const fetchStatistics = async () => {
      try {
        const response = await adminApi.getUserStatistics();
        Object.assign(statistics, response.data.data);
      } catch (error) {
        console.error("Error fetching statistics:", error);
      }
    };

    const fetchPermissions = async () => {
      try {
        const response = await adminApi.getPermissions();
        permissions.value = response.data.data;
      } catch (error) {
        console.error("Error fetching permissions:", error);
        toast.error("Lỗi khi tải danh sách quyền");
      }
    };

    let searchTimeout;
    const debounceSearch = () => {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        pagination.current_page = 1;
        fetchUsers();
      }, 500);
    };

    const resetFilters = () => {
      filters.search = "";
      filters.role = "";
      filters.status = "";
      pagination.current_page = 1;
      fetchUsers();
    };

    const changePage = (page) => {
      if (page < 1 || page > pagination.last_page) return;
      pagination.current_page = page;
      fetchUsers();
    };

    const toggleSelectAll = () => {
      if (isAllSelected.value) {
        selectedUsers.value = [];
      } else {
        selectedUsers.value = users.value.map((u) => u.id);
      }
    };

    const viewUserTrashed = () => {
      showUserTrashed.value = true;
    };

    const viewUser = (user) => {
      selectedUser.value = user;
      showDetailModal.value = true;
    };

    const editUser = (user) => {
      selectedUser.value = user;
      showDetailModal.value = false;
      showEditModal.value = true;
    };

    const managePermissions = (user) => {
      selectedUser.value = user;
      showPermissionsModal.value = true;
    };

    const toggleStatus = (user) => {
      const newStatus = user.status === "active" ? "suspended" : "active";
      confirmDialog.title =
        newStatus === "suspended"
          ? "Tạm ngưng tài khoản"
          : "Kích hoạt tài khoản";
      confirmDialog.message = `Bạn có chắc muốn ${
        newStatus === "suspended" ? "tạm ngưng" : "kích hoạt"
      } tài khoản "${user.name}"?`;
      confirmDialog.type = newStatus === "suspended" ? "warning" : "success";
      confirmDialog.onConfirm = async () => {
        try {
          await adminApi.changeUserStatus(user.id, newStatus);
          toast.success(
            `Đã ${
              newStatus === "suspended" ? "tạm ngưng" : "kích hoạt"
            } tài khoản`,
          );
          fetchUsers();
          fetchStatistics();
          showConfirmDialog.value = false;
        } catch (error) {
          toast.error("Có lỗi xảy ra");
        }
      };
      showConfirmDialog.value = true;
    };

    const deleteUser = (user) => {
      confirmDialog.title = "Xóa tài khoản";
      confirmDialog.message = `Bạn có chắc muốn xóa tài khoản "${user.name}"? Hành động này có thể khôi phục.`;
      confirmDialog.type = "danger";
      confirmDialog.onConfirm = async () => {
        try {
          await adminApi.deleteUser(user.id);
          toast.success("Đã xóa tài khoản");
          fetchUsers();
          fetchStatistics();
          showConfirmDialog.value = false;
        } catch (error) {
          const errorMessage =
            error.response?.data?.message || "Có lỗi xảy ra khi xóa tài khoản";
          toast.error(errorMessage);
        }
      };
      showConfirmDialog.value = true;
    };

    const handleUserCreated = () => {
      fetchUsers();
      fetchStatistics();
      toast.success("Tạo tài khoản thành công");
    };

    const handleUserUpdated = () => {
      showEditModal.value = false;
      fetchUsers();
      fetchStatistics();
      toast.success("Cập nhật tài khoản thành công");
    };

    const handlePermissionsUpdated = () => {
      showPermissionsModal.value = false;
      fetchUsers();
    };

    const handleRestoreUser = () => {
        showUserTrashed.value = false;
        fetchUsers();
        fetchStatistics();
    }

    // Helper functions
    const getAvatarUrl = (avatar) => {
      const baseUrl = process.env.VUE_APP_API_BASE_URL;
      return `${baseUrl}/storage/${avatar}`;
    };

    const getInitials = (name) => {
      if (!name) return "";
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .substring(0, 2);
    };

    const getRoleLabel = (role) => {
      const labels = {
        super_admin: "Super Admin",
        admin: "Admin",
        manager: "Manager",
        staff: "Nhân viên",
      };
      return labels[role] || role;
    };

    const getStatusLabel = (status) => {
      const labels = {
        active: "Hoạt động",
        inactive: "Không hoạt động",
        suspended: "Tạm ngưng",
      };
      return labels[status] || status;
    };

    const formatDate = (date) => {
      return new Date(date).toLocaleString("vi-VN");
    };

    // Lifecycle
    onMounted(() => {
      fetchUsers();
      fetchStatistics();
      fetchPermissions();
    });

    return {
      loading,
      users,
      selectedUsers,
      selectedUser,
      statistics,
      filters,
      pagination,
      currentUser,
      showCreateModal,
      showEditModal,
      showDetailModal,
      showPermissionsModal,
      showConfirmDialog,
      confirmDialog,
      showUserTrashed,
      isAllSelected,
      visiblePages,
      fetchUsers,
      debounceSearch,
      resetFilters,
      changePage,
      toggleSelectAll,
      viewUserTrashed,
      viewUser,
      editUser,
      managePermissions,
      toggleStatus,
      deleteUser,
      handleUserCreated,
      handleUserUpdated,
      handlePermissionsUpdated,
      handleRestoreUser,
      getAvatarUrl,
      getInitials,
      getRoleLabel,
      getStatusLabel,
      formatDate,
      permissions,
    };
  },
};
</script>

<style scoped>
/* Main Layout */
.account-page {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

/* Header */
.page-header {
  margin-bottom: 2rem;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 0.5rem 0;
}

.page-subtitle {
  color: #718096;
  margin: 0;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
}

.stat-icon.bg-blue {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}
.stat-icon.bg-green {
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
}
.stat-icon.bg-orange {
  background: linear-gradient(135deg, #ed8936 0%, #dd6b20 100%);
}
.stat-icon.bg-red {
  background: linear-gradient(135deg, #f56565 0%, #e53e3e 100%);
}

.stat-info {
  flex: 1;
}

.stat-label {
  font-size: 0.875rem;
  color: #718096;
  margin: 0 0 0.25rem 0;
}

.stat-value {
  font-size: 1.875rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
}

/* Filters */
.filters-section {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.search-box {
  flex: 1;
  min-width: 300px;
  position: relative;
}

.search-box i {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
}

.search-box input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: border-color 0.2s;
}

.search-box input:focus {
  outline: none;
  border-color: #667eea;
}

.filter-group {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.filter-select {
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  cursor: pointer;
  min-width: 150px;
}

/* Table */
.table-container {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;
}

.users-table {
  width: 100%;
  border-collapse: collapse;
}

.users-table thead {
  background: #f7fafc;
}

.users-table th {
  padding: 1rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.users-table tbody tr {
  border-bottom: 1px solid #e2e8f0;
  transition: background-color 0.2s;
}

.users-table tbody tr:hover {
  background-color: #f7fafc;
}

.users-table tbody tr.selected {
  background-color: #edf2f7;
}

.users-table td {
  padding: 1rem;
}

/* User Info */
.user-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.user-avatar {
  position: relative;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  overflow: hidden;
}

.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
}

.user-details {
  flex: 1;
}

.user-name {
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 0.25rem 0;
}

.user-email {
  font-size: 0.875rem;
  color: #718096;
  margin: 0 0 0.125rem 0;
}

.user-username {
  font-size: 0.75rem;
  color: #a0aec0;
  margin: 0;
}

/* Badges */
.role-badge,
.status-badge {
  display: inline-block;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
}

.role-super_admin {
  background: #fef5e7;
  color: #f39c12;
}
.role-admin {
  background: #ebf8ff;
  color: #3182ce;
}
.role-manager {
  background: #f0fff4;
  color: #38a169;
}
.role-staff {
  background: #faf5ff;
  color: #805ad5;
}

.status-active {
  background: #f0fff4;
  color: #38a169;
}
.status-inactive {
  background: #fffaf0;
  color: #dd6b20;
}
.status-suspended {
  background: #fff5f5;
  color: #e53e3e;
}

.permissions-preview {
  font-size: 0.875rem;
}

.permission-count {
  color: #4299e1;
  font-weight: 500;
}

.no-permissions {
  color: #a0aec0;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.btn-icon {
  width: 2rem;
  height: 2rem;
  border: none;
  background: #f7fafc;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  color: #4a5568;
}

.btn-icon:hover {
  background: #edf2f7;
  transform: scale(1.1);
}

.btn-icon.text-danger {
  color: #e53e3e;
}
.btn-icon.text-warning {
  color: #dd6b20;
}
.btn-icon.text-success {
  color: #38a169;
}

/* Buttons */
.btn {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-outline {
  background: white;
  border: 1px solid #e2e8f0;
  color: #4a5568;
}

.btn-outline:hover {
  background: #f7fafc;
}

.btn-sm {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
}

.btn-reset-icon .fa {
  color: white;
}
/* Pagination */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.pagination-info {
  color: #718096;
  font-size: 0.875rem;
}

.pagination-controls {
  display: flex;
  gap: 0.5rem;
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 4rem 2rem;
  width: 100vw;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  background-color: rgba(0, 0, 0, 0.5);
}

.spinner {
  width: 3rem;
  height: 3rem;
  border: 3px solid #e2e8f0;
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: #a0aec0;
}

.empty-state i {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-state h3 {
  color: #4a5568;
  margin-bottom: 0.5rem;
}

/* Utilities */
.text-muted {
  color: #a0aec0;
  font-size: 0.875rem;
}

.last-login {
  font-size: 0.875rem;
}

.login-time {
  color: #4a5568;
  margin: 0 0 0.25rem 0;
}

.login-ip {
  color: #a0aec0;
  margin: 0;
  font-size: 0.75rem;
}

/* Responsive */
@media (max-width: 768px) {
  .account-page {
    padding: 1rem;
  }

  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .filters-section {
    flex-direction: column;
  }

  .search-box {
    min-width: 100%;
  }

  .pagination {
    flex-direction: column;
    gap: 1rem;
  }
}
</style>
